import { useHistory } from "react-router-dom";
import { ComponentType, PropsWithChildren } from "react";
import * as React from "react";

export interface CleanHistoryProps {
  pushOrReplaceLocation: (changes: PushOrReplaceLocation) => void;
}

export interface PushOrReplaceLocation {
  pathname?: string | undefined;
  search?: string | undefined;
  state?: object | undefined;
  forceReplace?: boolean;
}

// Call this hook to get a function that can be used to push or replace the history location and avoid duplicate
// entries in the browser's window.history.
const useCleanHistory = () => {
  const history = useHistory();
  return (changes: PushOrReplaceLocation): void => {
    const { pathname, search, state } = history.location;
    const oldStateObject = typeof state === "object" ? state : {};
    const pathnameToSet = changes.pathname || pathname;
    const searchToSet = changes.search || search;
    const stateToSet = changes.state || {};
    // The search string from the history location often has a leading '?'
    if (!changes.forceReplace && (changes.pathname !== undefined && pathname !== changes.pathname || changes.search !== undefined && search?.replaceAll("?", "") !== changes.search)) {
      history.push({
        ...history.location,
        pathname: pathnameToSet,
        search: searchToSet,
        state: { ...oldStateObject, ...stateToSet },
      });
    } else {
      history.replace({
        ...history.location,
        pathname: pathnameToSet,
        search: searchToSet,
        state: { ...oldStateObject, ...stateToSet },
      });
    }
  }
}

export default useCleanHistory;

export function withCleanHistory<T>(Component: ComponentType<PropsWithChildren<T & CleanHistoryProps>>): ComponentType<PropsWithChildren<T>> {
  return function WrappedComponent(props) {
    const cleanHistory = useCleanHistory();
    return <Component {...props} {...{ ['pushOrReplaceLocation']: cleanHistory }} />;
  }
}
